import { ChangeEvent, JSX, useEffect, useRef, useState } from 'react';
import { EmailFinder } from '../../../../../types/entities';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Cancel as CancelIcon,
    OpenInNew as OpenInNewIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNotify } from 'react-admin';
import { useEmailFinderCancel, useEmailFinderResultsLoader } from './hooks/email-finder-results-block.hooks';
import { formatDateTimeShort, shortUrl } from './email-finder-format';
import { StatusChip } from './components/StatusChip';
import { ScanSummary } from './components/ScanSummary';

const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 60 },
    // Kept for the CSV, which asks for every column, hidden on screen because the
    // person column beside it already carries the name.
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 110, editable: false },
    {
        // The address is the whole point of the tool, and at eight columns wide it
        // was the one that ellipsised, so it could not even be copied out. Name
        // over address in one column gives it the room, and the column is keyed on
        // the address so the export and the sort still mean what they say.
        field: 'email',
        headerName: 'Person',
        flex: 2,
        minWidth: 230,
        editable: false,
        renderCell: (params: any): JSX.Element => (
            <Box sx={ {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
                minWidth: 0,
            } }>
                <Box component="span"
                     title={ params.row.name }
                     sx={ {
                         fontSize: '13px',
                         lineHeight: 1.3,
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap',
                     } }>
                    { params.row.name }
                </Box>
                <Box component="span"
                     title={ params.row.email }
                     sx={ {
                         fontFamily: 'var(--wf-font-mono)',
                         fontSize: '11.5px',
                         lineHeight: 1.3,
                         color: 'text.secondary',
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap',
                     } }>
                    { params.row.email }
                </Box>
            </Box>
        ),
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 84,
        editable: false,
        align: 'center',
        headerAlign: 'center',
        // HQ-14. This used to paint its own literal colours - #1aea1a on #0b5e0b for new,
        // #4769ec on #0b1950 for existing, #eee on #000 otherwise. It survived both
        // themes because each pair carried its own foreground, so it was legible rather
        // than broken, and that is why the sweep that moved 55 borders onto `divider`
        // left it alone. It was still the one place in HQ where a status vocabulary
        // painted itself: that bright green had no relation to `--wf-ok`, which every
        // other success state uses. The tones now live in `email-finder-format.ts`, so
        // this chip and the scan list's chip cannot drift apart.
        renderCell: (params: any): JSX.Element => <StatusChip status={ params.row.status } dense />,
    },
    { field: 'jobTitle', headerName: 'Job title', flex: 1, minWidth: 110, editable: false },
    { field: 'department', headerName: 'Department', width: 108, editable: false },
    { field: 'telephone', headerName: 'Telephone', width: 118, editable: false },
    {
        field: 'url',
        headerName: 'Source page',
        flex: 1,
        minWidth: 120,
        editable: false,
        renderCell: (params: any): JSX.Element => (
            <a href={ params.row.url }
               target="_blank"
               rel="noreferrer"
               title={ params.row.url }
               style={ { fontSize: '12px' } }>
                { shortUrl(params.row.url) }
            </a>
        ),
    },
    { field: 'location', headerName: 'Location', width: 120, editable: false },
    { field: 'pageTitle', headerName: 'Page title', flex: 1, minWidth: 150, editable: false },
    {
        field: 'createdAt',
        headerName: 'Found',
        width: 106,
        editable: false,
        // The grid's own `dateTime` type wanted a Date back from a value getter and
        // then rendered it in the browser's own format, which disagreed with every
        // other stamp on the screen. One formatter now answers for all of them.
        renderCell: (params: any): JSX.Element => (
            <span style={ { fontSize: '12px' } }>{ formatDateTimeShort(params.row.createdAt) }</span>
        ),
    },
];

/**
 * Worth having, not worth the width. The pane is about 950px at a 1600 viewport,
 * which is three columns short of the eleven this grid can show, and the columns
 * button in the toolbar brings any of them back.
 */
const HIDDEN_BY_DEFAULT: Record<string, boolean> = {
    name: false,
    location: false,
    pageTitle: false,
    telephone: false,
};

const centred = {
    width: '100%',
    minHeight: '260px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
};

export const EmailFinderResultsBlock: (props: { emailFinder: EmailFinder | undefined }) => JSX.Element =
    (props: { emailFinder: EmailFinder | undefined }): JSX.Element => {
        const emailFinder: EmailFinder | undefined = props.emailFinder;
        const notify: any = useNotify();
        const [searchTerm, setSearchTerm] = useState<string>('');
        const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
        const [confirmCancel, setConfirmCancel] = useState<boolean>(false);
        const {
            loadingResults, emailFinderResults, loadEmailFinderResults, totalResults, resultsError,
        } = useEmailFinderResultsLoader();
        const { cancelling, cancelEmailFinder, cancellingError, cancelledSuccess } = useEmailFinderCancel();

        // A `let` timer inside the component body was a new variable on every
        // render, so the debounce it was meant to be never cleared anything.
        const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

        function handleSearchTermChange(e: ChangeEvent<HTMLInputElement>): void {
            const term: string = e.target.value;
            setSearchTerm(term);
            setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
            if (searchTimer.current !== null) {
                clearTimeout(searchTimer.current);
            }
            searchTimer.current = setTimeout((): void => {
                loadEmailFinderResults(emailFinder?.id, term, 1, paginationModel.pageSize);
            }, 400);
        }

        function handlePaginationModelChange(newModel: { page: number, pageSize: number }): void {
            setPaginationModel(newModel);
            loadEmailFinderResults(emailFinder?.id, searchTerm, newModel.page + 1, newModel.pageSize);
        }

        function handleEmailFinderCancel(): void {
            setConfirmCancel(false);
            cancelEmailFinder(emailFinder?.id, emailFinder?.adminUser);
        }

        useEffect((): (() => void) => {
            if (emailFinder !== undefined) {
                setSearchTerm('');
                setPaginationModel({ page: 0, pageSize: 10 });
                loadEmailFinderResults(emailFinder?.id, '', 1, 10);
            }

            return (): void => {
                if (searchTimer.current !== null) {
                    clearTimeout(searchTimer.current);
                }
            };
        // Keyed on the id, not the object. The container hands down a fresh object
        // every time it polls a running scan, and reloading on identity threw away
        // the search term and the page the admin was on every eight seconds.
        }, [emailFinder?.id]);

        // The cancel hook always reported its outcome into state that nothing read,
        // so a refused cancel looked exactly like one that worked.
        useEffect((): void => {
            if (cancelledSuccess) {
                notify('Scan cancelled', { type: 'info' });
            }
        }, [cancelledSuccess]);

        useEffect((): void => {
            if (cancellingError !== '') {
                notify(cancellingError, { type: 'error' });
            }
        }, [cancellingError]);

        if (emailFinder === undefined) {
            return (
                <Box sx={ {
                    ...centred,
                    height: '100%',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                } }>
                    <SearchIcon sx={ { fontSize: 30, color: 'text.secondary', opacity: 0.6 } } />
                    <Typography sx={ { fontSize: '14px', fontWeight: 600 } }>
                        { 'No scan selected' }
                    </Typography>
                    <Typography sx={ { fontSize: '12.5px', color: 'text.secondary' } }>
                        { 'Pick a scan on the left to see the emails it found.' }
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={ { width: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 } }>
                <Box sx={ {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: '12px',
                } }>
                    <Box sx={ { minWidth: 0 } }>
                        <Box sx={ { display: 'flex', alignItems: 'center', gap: '10px' } }>
                            <Typography component="h3"
                                        sx={ { fontSize: '17px', fontWeight: 600, margin: 0 } }>
                                { emailFinder?.name }
                            </Typography>
                            <StatusChip status={ emailFinder?.status } />
                        </Box>
                        { emailFinder?.targetUrl !== undefined &&
                            <a href={ emailFinder.targetUrl }
                               target="_blank"
                               rel="noreferrer"
                               style={ {
                                   display: 'inline-flex',
                                   alignItems: 'center',
                                   gap: '4px',
                                   fontSize: '12px',
                                   fontFamily: 'var(--wf-font-mono)',
                                   marginTop: '3px',
                               } }>
                                { shortUrl(emailFinder.targetUrl) }
                                <OpenInNewIcon sx={ { fontSize: 13 } } />
                            </a>
                        }
                    </Box>

                    { emailFinder.status !== 'complete' &&
                        <Tooltip title="Stop this scan">
                            <span>
                                <Button variant="outlined"
                                        color="error"
                                        disabled={ cancelling }
                                        startIcon={ <CancelIcon /> }
                                        onClick={ (): void => setConfirmCancel(true) }
                                        sx={ { whiteSpace: 'nowrap' } }>
                                    { cancelling ? 'Cancelling' : 'Cancel scan' }
                                </Button>
                            </span>
                        </Tooltip>
                    }
                </Box>

                <ScanSummary finder={ emailFinder } />

                <TextField value={ searchTerm }
                           size="small"
                           type="search"
                           disabled={ loadingResults && emailFinderResults?.length === 0 }
                           onChange={ handleSearchTermChange }
                           placeholder="Search names, emails, job titles"
                           slotProps={ {
                               input: {
                                   startAdornment: (
                                       <InputAdornment position="start">
                                           <SearchIcon sx={ { fontSize: 18, color: 'text.secondary' } } />
                                       </InputAdornment>
                                   ),
                               },
                           } }
                           sx={ { width: '340px', maxWidth: '100%', marginBottom: '12px' } } />

                { resultsError !== '' &&
                    <Box sx={ {
                        ...centred,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        color: 'error.main',
                    } }>
                        <Typography sx={ { fontSize: '13px' } }>{ resultsError }</Typography>
                    </Box>
                }

                { resultsError === '' &&
                    // react-admin's `.layout` carries `min-width: fit-content`, so the
                    // grid's columns push the whole admin wider than the window: nine
                    // columns of minimum width became a 1895px page inside a 1600px
                    // viewport, with a horizontal scrollbar under the sidebar and all.
                    // A zero width with a percentage minimum fills the pane exactly as
                    // `width: 100%` did, and contributes nothing to that intrinsic
                    // measurement. `overflow: hidden` on either wrapper does not help.
                    <Box sx={ { width: 0, minWidth: '100%' } }>
                        <DataGrid
                            rows={ emailFinderResults ?? [] }
                            columns={ columns }
                            loading={ loadingResults }
                            pagination
                            pageSizeOptions={ [10, 20, 50, 100] }
                            rowCount={ totalResults }
                            paginationModel={ paginationModel }
                            onPaginationModelChange={ handlePaginationModelChange }
                            paginationMode="server"
                            rowHeight={ 52 }
                            columnHeaderHeight={ 44 }
                            // The grid lays its flex columns out across the viewport
                            // including the vertical scrollbar it is about to draw, so a
                            // fixed height left every column 23px wider than the room they
                            // had and put a horizontal scrollbar under the last one. Growing
                            // to fit the page removes the vertical scrollbar and the
                            // arithmetic comes out right.
                            autoHeight
                            disableColumnFilter
                            disableRowSelectionOnClick
                            showToolbar
                            initialState={ { columns: { columnVisibilityModel: HIDDEN_BY_DEFAULT } } }
                            slotProps={ {
                                toolbar: {
                                    csvOptions: {
                                        fileName: new Date().toISOString().replace(/[:.]/g, '-'),
                                        // Everything the scan found, not only the columns
                                        // that fit on the screen.
                                        allColumns: true,
                                    },
                                },
                            } }
                            sx={ {
                                border: '1px solid',
                                borderColor: 'divider',
                                '& .MuiDataGrid-columnHeaders': { background: 'var(--wf-surface-2)' },
                                '& .MuiDataGrid-cell': { alignItems: 'center' },
                            } }
                        />
                    </Box>
                }

                <Dialog open={ confirmCancel } onClose={ (): void => setConfirmCancel(false) }>
                    <DialogTitle>{ 'Cancel this scan?' }</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={ { fontSize: '14px' } }>
                            { `"${emailFinder?.name}" will stop where it is. The emails it has already `
                                + 'found are kept, and the scan cannot be resumed.' }
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={ (): void => setConfirmCancel(false) }>{ 'Keep running' }</Button>
                        <Button color="error" variant="contained" disableElevation
                                onClick={ handleEmailFinderCancel }>
                            { 'Cancel scan' }
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }
